/**
 * What the writer has highlighted in the script document, and the pure
 * arithmetic over it. No DOM, no React — ScriptView owns the events, this owns
 * the meaning.
 *
 * WHY THERE ARE TWO KINDS. Every script block is its own `<textarea>` (the
 * mirrored-highlight trick in ScriptView needs one), and a browser text
 * selection can never span two of them: the moment you drag out of a textarea,
 * it keeps extending its OWN selection and the next one never hears about it.
 * So "highlight three lines" cannot be a character range the way "highlight
 * three words" can.
 *
 * The answer is to change what a cross-block drag MEANS rather than fight the
 * platform: inside one block it stays an exact character range (`text`), and
 * the moment it crosses into another block it becomes a range of WHOLE LINES
 * (`blocks`). That matches what a person dragging down a script is asking for
 * anyway — nobody wants "the last four words of line 3 through the first two of
 * line 7" — and it is honest about what it can deliver, which a partial
 * character range faked across textareas would not be.
 */

/** Live highlight in the script document. */
export type ScriptSelection =
  | {
      kind: "text";
      /** The one block the range lives in. */
      rowId: string;
      start: number;
      end: number;
    }
  | {
      kind: "blocks";
      /** Where the drag began — may be BELOW `focusRowId` (an upward drag). */
      anchorRowId: string;
      /** Where the pointer is now. */
      focusRowId: string;
      /**
       * Rows picked one at a time with Cmd/Ctrl+click, which need not touch the
       * anchor→focus run. Kept beside the range rather than replacing it so a
       * plain drag stays exactly the two-endpoint value it always was.
       */
      addedRowIds?: readonly string[];
    };

/**
 * The rows a selection covers, in DOCUMENT order — never drag order or click
 * order, so an upward drag and a downward drag over the same lines produce the
 * same context. Rows that have since been deleted drop out (C4: a collaborator
 * can remove a row out from under a live selection); when that leaves the
 * anchor→focus range unresolvable, only the surviving Cmd+clicked rows remain.
 */
export function selectedRowIds(
  selection: ScriptSelection,
  rowIds: readonly string[],
): string[] {
  if (selection.kind === "text") {
    return rowIds.includes(selection.rowId) ? [selection.rowId] : [];
  }
  const a = rowIds.indexOf(selection.anchorRowId);
  const b = rowIds.indexOf(selection.focusRowId);
  const inRange =
    a < 0 || b < 0 ? [] : a <= b ? rowIds.slice(a, b + 1) : rowIds.slice(b, a + 1);
  if (!selection.addedRowIds?.length) return inRange;
  // Union, re-sorted into document order by walking rowIds once.
  const wanted = new Set([...inRange, ...selection.addedRowIds]);
  return rowIds.filter((rowId) => wanted.has(rowId));
}

/**
 * Add or remove one row from a block selection (Cmd/Ctrl+click).
 *
 * The result is always stored in the explicit form — a degenerate one-row range
 * plus `addedRowIds` — because that is the only shape that can represent an
 * arbitrary set, including one whose rows were carved out of the middle of a
 * previous drag. Two-endpoint ranges are what drags and Shift+click produce;
 * this collapses to the general form the moment a set stops being contiguous.
 * Returns null when the last row was toggled off.
 */
export function toggleBlockInSelection(
  selection: ScriptSelection | null,
  rowId: string,
  rowIds: readonly string[],
): ScriptSelection | null {
  const current =
    selection === null || selection.kind === "text"
      ? []
      : selectedRowIds(selection, rowIds);
  const next = current.includes(rowId)
    ? current.filter((id) => id !== rowId)
    : [...current, rowId];
  if (next.length === 0) return null;
  const ordered = rowIds.filter((id) => next.includes(id));
  const [first, ...rest] = ordered as [string, ...string[]];
  return { kind: "blocks", anchorRowId: first, focusRowId: first, addedRowIds: rest };
}

/**
 * The highlighted TEXT, one entry per line, ready to send as context.
 *
 * A `text` selection contributes its exact substring — split on newlines,
 * because a single block can hold soft-wrapped lines the writer typed with
 * Shift+Enter. A `blocks` selection contributes each row's whole cell. Blank
 * lines are dropped either way: they carry no signal and would only spend the
 * request's character budget.
 */
export function selectionLines(
  selection: ScriptSelection,
  rowIds: readonly string[],
  textOf: (rowId: string) => string,
): string[] {
  if (selection.kind === "text") {
    const text = textOf(selection.rowId);
    const start = Math.max(0, Math.min(selection.start, selection.end));
    const end = Math.min(text.length, Math.max(selection.start, selection.end));
    return splitLines(text.slice(start, end));
  }
  const lines: string[] = [];
  for (const rowId of selectedRowIds(selection, rowIds)) {
    lines.push(...splitLines(textOf(rowId)));
  }
  return lines;
}

/** True when the highlight covers nothing a question could be about. */
export function isEmptySelection(
  selection: ScriptSelection | null,
  rowIds: readonly string[],
  textOf: (rowId: string) => string,
): boolean {
  if (selection === null) return true;
  return selectionLines(selection, rowIds, textOf).length === 0;
}

function splitLines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
